'use es6';

import { List, Map as ImmutableMap } from 'immutable';
import { TIME_SERIES } from 'reporting-data/constants/configTypes';
import { UNIFIED_EVENTS } from 'reporting-data/constants/dataTypes';
import { fromMetricKey, toMetricKey } from 'reporting-data/v2/dataset/datasetMetrics';
import { pickTokenizedColors, pickPatterns } from 'reporting-ui-components/reports/utils/colors/colorPicker';
import { getDataKeys } from 'reporting-visualizations/highcharts/chartUtils';
import { ColorEncoding, Style } from 'reporting-visualizations/visualizations/xy/xyRecords';
import { isBrandKitTheme } from 'reporting-ui-components/reports/utils/colors/brandKitUtils';
import { getPaletteColorScheme } from 'reporting-ui-components/components/PlatformReport/v2/buildViz/colorUtils';
import { applySlicedColorsToStyle, applySlicedColorsToLayer } from 'reporting-ui-components/components/PlatformReport/v2/buildViz/slicedVisualization';
const stringifyDataKeys = keys => keys.map(k => String(k));
export const applyXYColors = (visualization, report, reportingThemes, brandKitPalettes) => {
  let colorConfig = report.getIn(['displayParams', 'colors'], ImmutableMap({}));
  const isUnifiedEventsReport = report.getIn(['config', 'dataType']) === UNIFIED_EVENTS;
  const configType = report.getIn(['config', 'configType']);
  const dimensions = report.getIn(['config', 'dimensions']) || List();
  const metrics = report.getIn(['config', 'metrics']).flatMap(metric => isUnifiedEventsReport ? List([metric.get('property')]) : metric.get('metricTypes').map(type => toMetricKey({
    property: metric.get('property'),
    type
  })));
  const metricToMetricTypesMap = report.getIn(['config', 'metrics']).toMap().mapKeys((_, metric) => metric.get('property')).map(metric => metric.get('metricTypes'));
  const isBrandKitPalette = isBrandKitTheme(colorConfig.get('theme'));
  const theme = getPaletteColorScheme({
    colorConfig,
    brandKitPalettes,
    reportingThemes
  });
  if (theme.get('colorsTheme') !== undefined) {
    colorConfig = colorConfig.set('theme', theme.get('colorsTheme'));
  }
  visualization = visualization.update('style', (style = Style({})) => style.set('colors', theme));
  if (colorConfig.get('custom') && dimensions.count() === 1) {
    return visualization.update('layers', layers => layers.map(layer => {
      if (layer.get('dataset') === 'compare') {
        return layer;
      }
      if (metrics.count() === 1 && configType !== TIME_SERIES) {
        return layer.set('color', ColorEncoding({
          field: layer.getIn(['x', 'field']),
          defined: colorConfig.get('custom')
        }));
      }
      const yProperty = fromMetricKey(layer.getIn(['y', 'field'])).property;
      return layer.set('color', ColorEncoding({
        default: colorConfig.getIn(['custom', yProperty])
      }));
    }));
  }
  const primaryColorField = visualization.getIn(['layers', 0, 'color', 'field']);
  const colorKeys = primaryColorField ? stringifyDataKeys(getDataKeys(visualization.getIn(['data', 'primary']), primaryColorField)) : metrics;
  const pickedColors = primaryColorField ? pickTokenizedColors({
    colorConfig,
    keys: colorKeys,
    customTheme: isBrandKitPalette && theme || undefined,
    reportingThemes
  }) : pickTokenizedColors({
    colorConfig: colorConfig.update('custom', custom => custom ? custom.reduce((colors, color, key) => colors.merge(metricToMetricTypesMap.get(key, List()).toMap().mapKeys((_, v) => toMetricKey({
      property: key,
      type: v
    })).map(() => color)), ImmutableMap()) : custom),
    keys: colorKeys,
    customTheme: isBrandKitPalette && theme || undefined,
    reportingThemes
  });
  const definedPatterns = theme.get('patterns') ? pickPatterns(theme.get('patterns'), colorKeys) : undefined;
  const definedDashStyles = theme.get('dashStylePatterns') ? pickPatterns(theme.get('dashStylePatterns'), colorKeys) : undefined;
  return visualization.update('style', (style = Style({})) => style.set('colors', theme)).update('layers', layers => layers.map(layer => {
    return layer.update('color', (color = ColorEncoding({})) => {
      return ColorEncoding(Object.assign({}, color.toJS(), {
        defined: color.get('default') ? color.get('defined') : pickedColors,
        definedPatterns,
        definedDashStyles
      }));
    });
  }));
};
export const applySlicedColors = (visualization, report, reportingThemes, brandKitPalettes) => {
  const colorConfig = report.getIn(['displayParams', 'colors'], ImmutableMap({})).toJS();
  const isUnifiedEventsReport = report.getIn(['config', 'dataType']) === UNIFIED_EVENTS;
  const metrics = report.getIn(['config', 'metrics']).flatMap(metric => isUnifiedEventsReport ? List([metric.get('property')]) : metric.get('metricTypes').map(type => toMetricKey({
    property: metric.get('property'),
    type
  })));
  const primaryColorField = visualization.getIn(['layer', 'color', 'field']);
  const updatedStyle = applySlicedColorsToStyle({
    colorConfig,
    reportingThemes,
    brandKitPalettes
  });
  const updatedLayer = applySlicedColorsToLayer({
    primaryData: visualization.getIn(['data', 'primary']),
    metrics,
    originalReport: report,
    primaryColorField,
    reportingThemes,
    brandKitPalettes,
    colorConfig
  });
  visualization = visualization.update('style', (style = Style({})) => style.set('colors', updatedStyle.colors));
  visualization = visualization.set('layer', visualization.get('layer').update('color', (color = ColorEncoding({})) => color.get('default') ? color : color.set('defined', updatedLayer.color.defined)).update('color', (color = ColorEncoding({})) => color.set('definedPatterns', updatedLayer.color.definedPatterns)));
  return visualization;
};